import React, { useEffect, useRef } from "react";
import "./cursor.css";

/**
 * Subtle two-layer custom cursor (desktop only).
 *   - 5px white dot, follows pointer exactly
 *   - 28px ring, lags slightly for smooth trail
 * On any interactive element body.cursor-hover is set, which scales both up and
 * tints them blue.
 */
const CustomCursor: React.FC = () => {
  const dotRef = useRef<HTMLDivElement | null>(null);
  const ringRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isTouch = "ontouchstart" in window || (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 0;
    if (isTouch || window.innerWidth < 1024) return;

    document.documentElement.classList.add("iav-has-custom-cursor");

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let ringX = mouseX;
    let ringY = mouseY;
    let raf = 0;
    let lastHover = false;

    const onMove = (e: MouseEvent) => { mouseX = e.clientX; mouseY = e.clientY; };

    const isHover = (): boolean => {
      const el = document.elementFromPoint(mouseX, mouseY) as HTMLElement | null;
      if (!el) return false;
      return !!el.closest('a, button, [role="button"], [data-cursor="cta"], [data-cursor="link"], input, textarea, select, label');
    };

    const tick = () => {
      if (dotRef.current) dotRef.current.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0) translate(-50%, -50%)`;
      ringX += (mouseX - ringX) * 0.22;
      ringY += (mouseY - ringY) * 0.22;
      if (ringRef.current) ringRef.current.style.transform = `translate3d(${ringX}px, ${ringY}px, 0) translate(-50%, -50%)`;
      const sp = document.getElementById('iav-cursor-spotlight');
      if (sp) { sp.style.left = mouseX + 'px'; sp.style.top = mouseY + 'px'; }
      const hov = isHover();
      if (hov !== lastHover) {
        document.body.classList.toggle("cursor-hover", hov);
        lastHover = hov;
      }
      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    window.addEventListener("mousemove", onMove);

    const onLeave = () => { if (dotRef.current) dotRef.current.style.opacity = "0"; if (ringRef.current) ringRef.current.style.opacity = "0"; };
    const onEnter = () => { if (dotRef.current) dotRef.current.style.opacity = ""; if (ringRef.current) ringRef.current.style.opacity = ""; };
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      document.documentElement.classList.remove("iav-has-custom-cursor");
      document.body.classList.remove("cursor-hover");
    };
  }, []);

  return (
    <>
      <div id="cursorRing" ref={ringRef} aria-hidden data-testid="custom-cursor-ring" />
      <div id="cursorDot" ref={dotRef} aria-hidden data-testid="custom-cursor-dot" />
      <div id="iav-cursor-spotlight" aria-hidden="true" />
    </>
  );
};

export default CustomCursor;
